import { useMemo } from "react";
import { ColumnDef } from "@components/spreadsheet/types";
import {
    buildPeriodColumns,
    getDefaultExpandedIds,
} from "@components/tasks/TaskBody/ProfitLossActivation/profitLossActivationUtils";
import { buildAmountCellFn } from "@components/tasks/TaskBody/ProfitLossActivation/profitLossTaskColumns";
import { useProfitLossReport } from "@components/tasks/TaskBody/ProfitLossActivation/useProfitLossReport";
import { LABEL_COLUMN } from "@components/tasks/TaskBody/shared/reportTaskColumns";
import { useExpandCollapseAll } from "@components/tasks/TaskBody/shared/useExpandCollapseAll";
import { TemplateReportGrid } from "./TemplateReportGrid";
import { TemplateReportQueryState } from "./TemplateReportQueryState";
import { TemplateReportTableProps } from "./templateReportTable.types";

export const TemplateProfitLossDataTable = ({
    entityId,
    dateRange,
    cycle,
    comparisonOptions,
    canSwitchEntity,
}: TemplateReportTableProps) => {
    const { profitAndLoss, rows, ledgerId, ledgersQuery } = useProfitLossReport(
        {
            entityId,
            dateRange,
            cycle,
            comparisonOptions,
            includeCounterpartyBreakdown: true,
        },
    );

    const defaultExpandedIds = useMemo(
        () => (rows ? getDefaultExpandedIds(rows) : []),
        [rows],
    );
    const expandState = useExpandCollapseAll(rows, defaultExpandedIds);
    const { expandedIds } = expandState;

    const columns = useMemo(
        (): ColumnDef[] => [
            LABEL_COLUMN,
            ...buildPeriodColumns(
                profitAndLoss.data?.periods ?? [],
                buildAmountCellFn({ expandedIds }),
                undefined,
            ),
        ],
        [profitAndLoss.data, expandedIds],
    );

    return (
        <TemplateReportQueryState
            queryResult={profitAndLoss}
            ledgerId={ledgerId}
            ledgersQuery={ledgersQuery}
            canSwitchEntity={canSwitchEntity}
            reportLabel="profit & loss"
        >
            {profitAndLoss.data && rows && (
                <TemplateReportGrid
                    rows={rows}
                    columns={columns}
                    expandState={expandState}
                />
            )}
        </TemplateReportQueryState>
    );
};
